import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { useBpV2State } from "../../context/BusinessPlanV2Context";
import { GlobalState } from "../../context/GlobalContext";
import { AuthState } from "context/AuthContext";

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";

import CreatePlanModal from "./CreatePlanModal/CreatePlanModal";
import { API, bpTableColumns, seasonOptions, brandList, canCreateBpPlan, getAllowedBpBrands } from "./data";
import { bpHomeGridSx } from "./StoreBp/storeBpTheme";
import { withHomeCellLayout } from "./StoreBp/gridCells";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const cardSx = {
	backgroundColor: "white",
	border: `1px solid ${grey[200]}`,
	borderRadius: 2.5,
	boxShadow: "0 4px 18px rgba(15, 23, 42, 0.045)",
	p: 2.5,
	flex: 1,
	minHeight: 0,
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
};

const BusinessPlanV2 = () => {
	const { allBp, setAllBp, setSelectedPlan } = useBpV2State();
	const { setLoadingOpen, setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const navigate = useNavigate();

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [filterSeason, setFilterSeason] = useState("All");
	const [filterYear, setFilterYear] = useState("All");
	const [filterBrand, setFilterBrand] = useState("All");
	const currentYear = new Date().getFullYear();
	const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i);
	const allowedBrands = getAllowedBpBrands(user?.role);
	const brandFilterOptions =
		allowedBrands === null ? brandList : brandList.filter((b) => allowedBrands.includes(b.key));
	const showNewPlan = canCreateBpPlan(user?.role);

	const fetchPlans = async () => {
		try {
			setLoadingOpen(true);
			const { data } = await axios.get(API.GET_PLANS);
			if (data.status === "success") {
				setAllBp(data.business_plan || []);
			}
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrMsgFromError(err), severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		fetchPlans();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filteredPlans = (allBp || []).filter((bp) => {
		if (filterSeason !== "All" && bp.season !== filterSeason) return false;
		if (filterYear !== "All" && bp.year !== filterYear) return false;
		if (filterBrand !== "All" && bp.brand !== filterBrand) return false;
		return true;
	});

	const handlePlanRowClick = (params) => {
		setSelectedPlan(params.row);
		navigate(`/explore/business_plan_v2/plan/${params.row.id}`);
	};

	return (
		<Box
			sx={{
				width: "1280px",
				maxWidth: "100%",
				margin: "auto",
				height: "calc(100vh - 72px)",
				boxSizing: "border-box",
				py: 2,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexShrink={0}>
				<Typography variant="h4" fontWeight={500} color={grey[900]}>
					Business Plan
				</Typography>
				{showNewPlan && (
					<Button variant="contained" size="large" startIcon={<AddRoundedIcon />} onClick={() => setShowCreateModal(true)}>
						New Plan
					</Button>
				)}
			</Stack>

			<Box sx={cardSx}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} flexShrink={0}>
					<Stack spacing={0.25}>
						<Typography variant="subtitle1" fontWeight={700}>
							Select a plan
						</Typography>
						<Typography variant="body2" color="text.secondary" fontSize={13}>
							Choose a season plan to review stores and submissions
						</Typography>
					</Stack>
					<Stack direction="row" spacing={2}>
						<FormControl size="small" sx={{ minWidth: 150 }}>
							<InputLabel>Brand</InputLabel>
							<Select label="Brand" value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}>
								<MenuItem value="All">All Brands</MenuItem>
								{brandFilterOptions.map((b) => (
									<MenuItem key={b.key} value={b.key}>
										{b.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 130 }}>
							<InputLabel>Season</InputLabel>
							<Select label="Season" value={filterSeason} onChange={(e) => setFilterSeason(e.target.value)}>
								<MenuItem value="All">All Seasons</MenuItem>
								{seasonOptions.map((s) => (
									<MenuItem key={s.key} value={s.key}>
										{s.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 110 }}>
							<InputLabel>Year</InputLabel>
							<Select label="Year" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
								<MenuItem value="All">All Years</MenuItem>
								{yearOptions.map((y) => (
									<MenuItem key={y} value={y}>
										{y}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Stack>

				<Box sx={{ flex: 1, minHeight: 0 }}>
					<DataGrid
						rows={filteredPlans}
						columns={withHomeCellLayout(bpTableColumns)}
						rowHeight={40}
						columnHeaderHeight={42}
						showCellVerticalBorder
						showColumnVerticalBorder
						disableRowSelectionOnClick
						disableColumnMenu
						onRowClick={handlePlanRowClick}
						sx={bpHomeGridSx}
					/>
				</Box>
			</Box>

			<CreatePlanModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onCreated={() => {
					fetchPlans();
				}}
			/>
		</Box>
	);
};

export default BusinessPlanV2;
